import React, { useEffect, useState } from "react";
import styles from "./PromptManager.module.css";
import PromptCard from "./components/cards/PromptCard";
import ConfirmationDialog from "./components/dialogs/ConfirmationDialog";
import AssetsPack from "../../../util/AssetsPack";
import { useNavigate } from "react-router-dom";
import LoadingSpinner from "../../../components/loading/LoadingSpinner";
import { FAV_TAG, Prompt } from "../../../api/responsePayload/PromptResponse";
import {
  showConsoleError,
  showConsoleMessage,
} from "../../../util/ConsoleMessage";
import {
  clarify_or_rewrite_question,
  generateInstructions,
  ocrInstructions,
} from "./components/Instructions";
import PromptModal from "./components/modals/PromptModal";
import FavModal from "./components/FavModal";
import { GetPrompts, PromptResetToDefault, PostPrompt, NodePatchTemp, PromptPatch } from "../../../api/NodePromptApiCall";

export const NODE_ORDER = [
  "ocr",
  "clarify_or_rewrite_question",
  "sop_grader",
  "generate",
];
export const NODE_DISPLAY_NAMES: { [key: string]: string } = {
  clarify_or_rewrite_question: "Clarify or Rewrite Question",
  ocr: "OCR",
  generate: "Generate",
  sop_grader: "SOP Grader",
};
export const NODES_TO_FETCH = [
  "clarify_or_rewrite_question",
  "ocr",
  "generate",
  "sop_grader",
];

export const NODE_INSTRUCTIONS: { [key: string]: string } = {
  clarify_or_rewrite_question: clarify_or_rewrite_question,
  ocr: ocrInstructions,
  generate: generateInstructions,
};

interface PromptManagerProps {}

const PromptManager: React.FC<PromptManagerProps> = ({}) => {
  const navigate = useNavigate();
  const [prompts, setPrompts] = useState<Prompt[] | null>(null);
  const [showResetAllToDefaultDialog, setShowResetAllToDefaultDialog] =
    useState(false);
  const [showFavDialog, setShowFavDialog] = useState(false);
  const [refresh, setRefresh] = useState(true);

  useEffect(() => {
    const fetchChat = async () => {
      try {
        const response = await GetPrompts(undefined, {
          node_name: NODES_TO_FETCH.join(","),
          is_active: true,
        });
        showConsoleMessage("GetPrompt response:", response?.results);
        response?.results &&
          setPrompts(
            response.results.sort((a, b) => {
              return (
                NODE_ORDER.indexOf(a.node.node_name) -
                NODE_ORDER.indexOf(b.node.node_name)
              );
            })
          );
      } catch (error) {
        showConsoleError("Failed to fetch data:", error);
      }
      setRefresh(false);
    };
    if (refresh) fetchChat();
  }, [refresh]);

  const handleResetAllApiCall = async () => {
    try {
      const response = await PromptResetToDefault(NODES_TO_FETCH);
      showConsoleMessage("PostPrompt Response:", response);
    } catch (error) {
      showConsoleError("Failed to PostPrompt data:", error);
    }
    setRefresh(true);
  };

  const handleResetAll = async () => {
    setShowResetAllToDefaultDialog(true);
  };

  const handleCreatePrompt = async (
    newNodeName: string,
    newPromptValue: string
  ) => {
    try {
      const response = await PostPrompt(newNodeName, newPromptValue);
      showConsoleMessage("PostPrompt Response:", response);
    } catch (error) {
      showConsoleError("Failed to PostPrompt data:", error);
    }
    setRefresh(true);
  };

  const handleResetToDefault = async (nodeName: string) => {
    try {
      const response = await PromptResetToDefault([nodeName]);
      showConsoleMessage("PostPrompt Response:", response);
    } catch (error) {
      showConsoleError("Failed to PostPrompt data:", error);
    }
    setRefresh(true);
  };

  const handleTempUpdate = async (node_id: number, value: number) => {
    try {
      const response = await NodePatchTemp(node_id, value);
      showConsoleMessage("NodePatchTemp Response:", response);
    } catch (error) {
      showConsoleError("Failed to NodePatchTemp data:", error);
    }
    setRefresh(true);
  };

  const handleOpenFavPrompts = () => {
    setShowFavDialog((prev) => !prev);
  };

  const handleSetFav = async (prompt_id: number, set: boolean) => {
    try {
      const response = await PromptPatch(
        prompt_id,
        undefined,
        undefined,
        undefined,
        undefined,
        set ? FAV_TAG : null
      );
      showConsoleMessage("handleSetFav Response:", response);
    } catch (error) {
      alert("Default Prompt Can not be changed");
      showConsoleError("Failed to handleSetFav data:", error);
    }
    setRefresh(true);
  };

  const handleSetDefault = async (prompt_id: number, is_default: boolean) => {
    try {
      const response = await PromptPatch(
        prompt_id,
        undefined,
        undefined,
        undefined,
        is_default
      );
      showConsoleMessage("handleSetDefault Response:", response);
    } catch (error) {
      alert("Default Prompt Can not be changed");
      showConsoleError("Failed to handleSetDefault data:", error);
    }
    setRefresh(true);
  };

  const handleBackButtonPress = () => {
    navigate("/customer-service");
  };

  return (
    <div className={styles.container}>
      <div className={styles.bottomContainer}>
        <div
          className={styles.backButtonContainer}
          onClick={handleBackButtonPress}
        >
          <img
            src={AssetsPack.icons.ICON_BACK.default}
            className={styles.back}
          />{" "}
          Go Back
        </div>
        <div className={styles.buttonsContainer}>
          <button className={styles.resetButton} onClick={handleResetAll}>
            Revert all to Default
          </button>
          <button className={styles.resetButton} onClick={handleOpenFavPrompts}>
            View All Favourite Prompts
          </button>
        </div>
      </div>
      <div className={styles.topContainer}>
        <div className={styles.stepContainer}>
          {prompts ? (
            prompts.map((prompt) => (
              <div className={styles.step} key={prompt.id}>
                <div className={styles.circle}>
                  {NODE_DISPLAY_NAMES[prompt.node.node_name] ||
                    prompt.node.node_name}
                </div>
              </div>
            ))
          ) : (
            <LoadingSpinner />
          )}
        </div>
        <div className={styles.promptCardContainer}>
          {prompts ? (
            prompts.map((prompt) => {
              return (
                <PromptCard
                  key={prompt.id}
                  prompt={prompt}
                  instruction={NODE_INSTRUCTIONS[prompt.node.node_name]}
                  onCreate={handleCreatePrompt}
                  resetToDefault={handleResetToDefault}
                  setRefresh={setRefresh}
                  setDefault={(value) => {
                    handleSetDefault(prompt.id, value);
                  }}
                  setFav={(value) => {
                    handleSetFav(prompt.id, value);
                  }}
                  tempUpdate={(value) => {
                    handleTempUpdate(prompt.node.id, value);
                  }}
                />
              );
            })
          ) : (
            <LoadingSpinner />
          )}
        </div>
      </div>

      <ConfirmationDialog
        title="Are you sure you want to reset all nodes to default?"
        isOpen={showResetAllToDefaultDialog}
        onCancel={() => {
          setShowResetAllToDefaultDialog(false);
        }}
        onConfirm={handleResetAllApiCall}
      />

      <PromptModal
        title="Favourite Prompts"
        isOpen={showFavDialog}
        onClose={() => {
          setShowFavDialog(false);
        }}
        onSave={() => {}}
        isAction={false}
      >
        <FavModal />
      </PromptModal>
    </div>
  );
};

export default PromptManager;
